import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Save, RotateCcw, Search, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';
import { DEFAULT_TRANSLATIONS } from '../../i18n/translations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TranslationsTab = ({ onMessage }) => {
  const [overrides, setOverrides] = useState({ en: {}, no: {} });
  // The server snapshot the tab was loaded with. Used to detect keys the admin
  // explicitly *cleared* so they can be sent to the backend as deletes.
  const [serverSnapshot, setServerSnapshot] = useState({ en: {}, no: {} });
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null); // {type:'ok'|'err', message, count}
  const statusTimer = useRef(null);

  useEffect(() => { load(); }, []);

  // Re-fetch when the user comes back to this tab — catches any inline edits
  // they made elsewhere since opening the bulk editor.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/translations`, {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      const en = data?.en || {};
      const no = data?.no || {};
      setOverrides({ en, no });
      setServerSnapshot({ en: { ...en }, no: { ...no } });
    } catch (e) {
      console.warn('Translations load failed', e?.message);
    }
  };

  const allKeys = useMemo(() => Object.keys(DEFAULT_TRANSLATIONS.en).sort(), []);

  const filtered = useMemo(() => {
    if (!search.trim()) return allKeys;
    const q = search.toLowerCase();
    return allKeys.filter(k =>
      k.toLowerCase().includes(q) ||
      (DEFAULT_TRANSLATIONS.en[k] || '').toLowerCase().includes(q) ||
      (DEFAULT_TRANSLATIONS.no[k] || '').toLowerCase().includes(q));
  }, [allKeys, search]);

  const valueFor = (lang, key) =>
    overrides[lang]?.[key] !== undefined ? overrides[lang][key] : DEFAULT_TRANSLATIONS[lang][key] || '';

  const setValue = (lang, key, val) => {
    setOverrides(prev => ({ ...prev, [lang]: { ...prev[lang], [key]: val } }));
  };

  const resetKey = (key) => {
    setOverrides(prev => {
      const en = { ...prev.en }; delete en[key];
      const no = { ...prev.no }; delete no[key];
      return { en, no };
    });
  };

  const showStatus = (status) => {
    setSaveStatus(status);
    if (statusTimer.current) clearTimeout(statusTimer.current);
    statusTimer.current = setTimeout(() => setSaveStatus(null), 6000);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Build the SET payload — keys whose current value differs from the
      // bundled default AND is non-empty.
      const enSets = {};
      const noSets = {};
      Object.entries(overrides.en || {}).forEach(([k, v]) => {
        if (v && v !== DEFAULT_TRANSLATIONS.en[k]) enSets[k] = v;
      });
      Object.entries(overrides.no || {}).forEach(([k, v]) => {
        if (v && v !== DEFAULT_TRANSLATIONS.no[k]) noSets[k] = v;
      });

      // Build the CLEAR payload — keys that existed in the server snapshot but
      // either (a) are no longer present in the local state (admin clicked reset)
      // or (b) have been blanked out by the admin.
      const clearEn = [];
      const clearNo = [];
      Object.keys(serverSnapshot.en || {}).forEach((k) => {
        const cur = overrides.en?.[k];
        if (cur === undefined || cur === '' || cur === DEFAULT_TRANSLATIONS.en[k]) clearEn.push(k);
      });
      Object.keys(serverSnapshot.no || {}).forEach((k) => {
        const cur = overrides.no?.[k];
        if (cur === undefined || cur === '' || cur === DEFAULT_TRANSLATIONS.no[k]) clearNo.push(k);
      });

      const totalChanges = Object.keys(enSets).length + Object.keys(noSets).length + clearEn.length + clearNo.length;
      if (totalChanges === 0) {
        showStatus({ type: 'ok', message: 'No changes to save.' });
        setSaving(false);
        return;
      }

      const { data } = await axios.put(
        `${API}/admin/translations`,
        { en: enSets, no: noSets, clear_en: clearEn, clear_no: clearNo },
        { withCredentials: true }
      );

      // Sync local state to the server's authoritative response so we don't
      // accidentally re-save stale data on the next click.
      const freshEn = data?.en || {};
      const freshNo = data?.no || {};
      setOverrides({ en: freshEn, no: freshNo });
      setServerSnapshot({ en: { ...freshEn }, no: { ...freshNo } });
      setLastSavedAt(new Date());

      // Broadcast so I18nProvider re-fetches and every <Tx> on every open page picks it up.
      window.dispatchEvent(new Event('translations-updated'));

      showStatus({
        type: 'ok',
        message: `Saved ${Object.keys(enSets).length} EN edit(s), ${Object.keys(noSets).length} NO edit(s)`
                  + (clearEn.length + clearNo.length ? `, cleared ${clearEn.length + clearNo.length} override(s)` : '')
                  + ' — live across the site.',
        count: totalChanges,
      });
      onMessage && onMessage('Translations saved — live across the site.');
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message;
      if (status === 401 || status === 403) {
        showStatus({ type: 'err', message: 'Your admin session expired — please log in again. Your changes were NOT saved.' });
      } else {
        showStatus({ type: 'err', message: `Save failed: ${detail}` });
      }
      onMessage && onMessage('Failed to save translations');
    } finally { setSaving(false); }
  };

  const reloadFromServer = async () => {
    await load();
    showStatus({ type: 'ok', message: 'Refreshed from server.' });
  };

  return (
    <div data-testid="admin-translations-tab" className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-xl font-bold text-slate-900 font-['Outfit']">Translations Editor</h3>
            <p className="text-sm text-slate-600 mt-1">
              Refine the bundled English/Norwegian copy. Empty overrides fall back to the defaults.
              {lastSavedAt && (
                <span className="block text-xs text-emerald-600 mt-0.5">
                  Last saved {lastSavedAt.toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="translations-refresh-btn"
              onClick={reloadFromServer}
              className="flex items-center gap-2 px-4 py-3 bg-white text-slate-700 border border-slate-200 rounded-full font-medium hover:bg-slate-50 text-sm"
              title="Re-fetch the current overrides from the server"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              data-testid="translations-save-btn"
              onClick={save}
              disabled={saving}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 shadow-sm disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>

        {saveStatus && (
          <div
            data-testid="translations-save-status"
            className={`mb-4 flex items-start gap-2 px-4 py-3 rounded-lg border ${
              saveStatus.type === 'ok'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-rose-50 border-rose-200 text-rose-800'
            }`}
          >
            {saveStatus.type === 'ok'
              ? <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
              : <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />}
            <span className="text-sm font-medium">{saveStatus.message}</span>
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid="translations-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by key or content…"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
        {filtered.length === 0 && (
          <p className="text-sm text-slate-500 py-8 text-center">No matching keys.</p>
        )}
        {filtered.map(key => {
          const enVal = valueFor('en', key);
          const noVal = valueFor('no', key);
          const enChanged = overrides.en?.[key] !== undefined && overrides.en[key] !== DEFAULT_TRANSLATIONS.en[key];
          const noChanged = overrides.no?.[key] !== undefined && overrides.no[key] !== DEFAULT_TRANSLATIONS.no[key];
          return (
            <div key={key} data-testid={`translation-row-${key}`}
                 className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors">
              <div className="flex items-center justify-between mb-3 gap-2">
                <code className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded font-mono truncate">{key}</code>
                {(enChanged || noChanged) && (
                  <button onClick={() => resetKey(key)}
                          className="flex items-center gap-1 text-xs text-slate-500 hover:text-red-600">
                    <RotateCcw className="w-3 h-3" /> reset
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">🇬🇧 English</label>
                  <textarea
                    data-testid={`translation-en-${key}`}
                    rows="2"
                    value={enVal}
                    onChange={(e) => setValue('en', key, e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500 ${
                      enChanged ? 'border-amber-400' : 'border-slate-200'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">🇳🇴 Norwegian</label>
                  <textarea
                    data-testid={`translation-no-${key}`}
                    rows="2"
                    value={noVal}
                    onChange={(e) => setValue('no', key, e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500 ${
                      noChanged ? 'border-amber-400' : 'border-slate-200'
                    }`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
